/**
 * External dependencies.
 */
import React from "react";
import classNames from "classnames";
import Image from "next/image";

/**
 * Internal dependencies.
 */
import { getStrapiMedia } from "utils";
import PartnerLogos from "components/partner-logos/partner-logos";
import Intro from "components/intro/intro";
import { MediaProp } from "types";
import Markdown from "components/markdown/markdown";
import LinkWithOptions from "components/link/link-with-options";
import { getDecorativeVideoSource } from "lib/decorative-video";

interface HomeIntroProp {
  priority?: boolean;
  pageHeading?: boolean;
  data: {
    hide?: boolean,
    Title?: string,
    Content?: string,
    Link1: {
      OpenInNewTab: boolean,
      RelNoFollow: boolean,
      Text: string,
      URL: string,
    },
    Link2: {
      OpenInNewTab: boolean,
      RelNoFollow: boolean,
      Text: string,
      URL: string,
    },
    Logos: {
      data?: ImageProp[],
    },
    Image?: MediaProp,
    Image2?: MediaProp,
    NoMargin?: boolean,
    Smaller?: boolean,
    FiftyFiftyGrid?: boolean,
    Video?: any,
    OfficialPartnerBadge?: boolean,
  };
}

interface ImageProp {
  id: number;
  attributes: {
    alternativeText?: string;
    url?: string;
    data: {
      attributes: {
        url: string,
      },
    },
  };
}

const HomeIntro: React.FC<HomeIntroProp> = ({ data, priority = false, pageHeading = false }) => (
  <Intro
    Smaller={data.Smaller}
    noMargin={data.NoMargin}
    fiftyGrid={data.FiftyFiftyGrid}
    className={classNames({
      "no-images": !data.Image?.data && !data.Image2?.data && !data.Video?.data,
    })}
  >
    <Intro.Content>
      {data?.OfficialPartnerBadge && (
        <div className="official-partner__badge">
          <Image alt="Shopify logo" src="/shopify.svg" width={15} height={16} />
          <span>Shopify Premier Partner</span>
        </div>
      )}
      {data.Title && <h1 className="h3">{data.Title}</h1>}
      {data.Content && <Markdown content={data.Content} primaryIntroHeading={pageHeading && !data.Title} />}
      {(data.Link1 || data.Link2) && (
        <div className="cta-row">
          {data.Link1 && (
            <LinkWithOptions data={data.Link1} className="btn btn--fill" />
          )}
          {data.Link2 && (
            <LinkWithOptions data={data.Link2} className="btn btn--white" />
          )}
        </div>
      )}
      {data.Logos?.data && (
        <PartnerLogos logos={data.Logos.data} compact />
      )}
    </Intro.Content>
    {(data.Image?.data || data.Image2?.data) && (
      <Intro.Image
        className={classNames({
          "two-images":
            data.Image && data.Image.data && data.Image2 && data.Image2.data,
        })}
      >
        {data.Image && data.Image.data && (
          <div className="image-1">
            <div className="image-1__inner">
              <Image
                src={getStrapiMedia(data.Image)}
                alt={data.Image.data.attributes.alternativeText || ""}
                width={data.Image.data.attributes.width}
                height={data.Image.data.attributes.height}
                sizes="(max-width: 767px) calc(100vw - 48px), (max-width: 1023px) calc(100vw - 70px), 704px"
                priority={priority}
              />
            </div>
            {data.Image2 && data.Image2.data && (
              <div className="image-2">
                <div className="image-2__inner">
                  <Image
                    src={getStrapiMedia(data.Image2)}
                    alt={data.Image2.data.attributes.alternativeText || ""}
                    width={data.Image2.data.attributes.width}
                    height={data.Image2.data.attributes.height}
                    sizes="(max-width: 1023px) 36vw, 256px"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Intro.Image>
    )}
    {data?.Video && data?.Video?.data && (
      <div className="intro__image">
        <div className="image-1">
          <div className="image-1__inner">
            <video
              src={getDecorativeVideoSource(getStrapiMedia(data?.Video))}
              width={data?.Video.data.attributes.width}
              height={data?.Video.data.attributes.height}
              autoPlay
              loop
              muted
              playsInline
            />
          </div>
        </div>
      </div>
    )}
  </Intro>
);

export default HomeIntro;
